import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { UserPlus, Mail, Shield, Loader2, Trash2, Copy, Check, X, ChevronDown } from 'lucide-react';
import { apiClient } from '../../api/client';

const DOMAIN_LABELS = {
  schools: 'Schools Management',
  corporates: 'Corporates Management',
  users: 'Users & Teens Management',
  leads: 'Lead Generation & Marketing',
  content: 'Content Management',
  analytics: 'Analytics & Reports',
  developer: 'Developer',
};

const DOMAIN_COLORS = {
  schools: '#2563EB',
  corporates: '#10B981',
  users: '#7B4FD8',
  leads: '#F5831F',
  content: '#EC4899',
  analytics: '#6366F1',
  developer: '#059669',
};

const AdminTeam = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', admin_domain: '' });

  const loadTeam = async () => {
    try {
      const data = await apiClient.request('/api/admin/team');
      setTeam(data.team || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { loadTeam(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!form.admin_domain) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const data = await apiClient.request('/api/admin/team/invite', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      setInviteResult(data);
      await loadTeam();
    } catch (e) {
      setInviteResult({ status: 'error', message: e.message || 'Failed to send invite' });
    }
    setInviting(false);
  };

  const updateMember = async (userId, updates) => {
    try {
      await apiClient.request(`/api/admin/team/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      await loadTeam();
    } catch (e) { console.error(e); }
  };

  const removeMember = async (userId, name) => {
    if (!window.confirm(`Remove ${name} from the team? This action cannot be undone.`)) return;
    try {
      await apiClient.request(`/api/admin/team/${userId}`, { method: 'DELETE' });
      await loadTeam();
    } catch (e) { console.error(e); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <AdminLayout pageTitle="Team Management">
      <CheatSheet pageId="team" />
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: '#1A1209' }} data-testid="team-page-title">Team Management</h1>
            <p className="text-sm mt-1" style={{ color: '#6B6257' }}>Invite sub-administrators and assign them to specific domains.</p>
          </div>
          <button
            onClick={() => { setShowInvite(!showInvite); setInviteResult(null); setForm({ email: '', name: '', admin_domain: '' }); }}
            className="px-4 py-2.5 rounded-lg text-white text-sm font-medium flex items-center gap-2 transition-all hover:shadow-lg"
            style={{ backgroundColor: '#F5831F' }}
            data-testid="invite-btn">
            <UserPlus size={16} /> Invite Sub-Admin
          </button>
        </div>

        {/* Invite Form */}
        {showInvite && (
          <div className="bg-white rounded-xl p-6 shadow-sm" style={{ border: '1px solid #E0E0E0' }} data-testid="invite-form">
            <h3 className="text-lg font-semibold mb-4" style={{ color: '#1A1209' }}>Invite New Team Member</h3>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: '#666' }}>Full Name</label>
                  <input
                    type="text" required value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm"
                    style={{ borderColor: '#E0E0E0' }}
                    placeholder="Jane Smith"
                    data-testid="invite-name-input" />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1.5" style={{ color: '#666' }}>Email</label>
                  <input
                    type="email" required value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm"
                    style={{ borderColor: '#E0E0E0' }}
                    placeholder="jane@company.com"
                    data-testid="invite-email-input" />
                </div>
              </div>

              <div>
                <label className="text-xs font-medium block mb-2" style={{ color: '#666' }}>Assign Domain</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(DOMAIN_LABELS).map(([id, label]) => (
                    <button key={id} type="button"
                      onClick={() => setForm(p => ({ ...p, admin_domain: id }))}
                      className={`p-3 rounded-lg border text-left transition-all ${form.admin_domain === id ? 'ring-2' : 'hover:border-gray-300'}`}
                      style={{
                        borderColor: form.admin_domain === id ? DOMAIN_COLORS[id] : '#E0E0E0',
                        ringColor: DOMAIN_COLORS[id],
                        backgroundColor: form.admin_domain === id ? `${DOMAIN_COLORS[id]}08` : '#FFF',
                      }}
                      data-testid={`domain-option-${id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DOMAIN_COLORS[id] }} />
                        <span className="text-xs font-bold capitalize" style={{ color: DOMAIN_COLORS[id] }}>{id}</span>
                      </div>
                      <p className="text-[11px]" style={{ color: '#666' }}>{label}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <button type="submit" disabled={inviting || !form.admin_domain}
                  className="px-5 py-2.5 rounded-lg text-white text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: '#F5831F' }}
                  data-testid="send-invite-btn">
                  {inviting ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : <><Mail size={14} /> Send Invite</>}
                </button>
                <button type="button" onClick={() => setShowInvite(false)}
                  className="px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">
                  Cancel
                </button>
              </div>
            </form>

            {/* Invite Result */}
            {inviteResult && (
              <div className={`mt-4 p-4 rounded-lg ${inviteResult.status === 'error' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="invite-result">
                {inviteResult.status === 'invited' ? (
                  <div>
                    <p className="text-sm font-medium text-green-800 mb-2">Invite sent! Share this setup link:</p>
                    <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-green-300">
                      <input type="text" readOnly value={inviteResult.setup_link}
                        className="flex-1 text-xs bg-transparent border-none outline-none text-gray-700" />
                      <button onClick={() => copyLink(inviteResult.setup_link)}
                        className="flex items-center gap-1 text-xs font-medium"
                        style={{ color: '#F5831F' }}
                        data-testid="copy-invite-link-btn">
                        {copiedLink ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                      </button>
                    </div>
                    <p className="text-[11px] text-green-700 mt-2">
                      Domain: <strong>{inviteResult.domain_label}</strong> — {form.name} will use this link to set their password.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-red-800">{inviteResult.message}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Team List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={{ border: '1px solid #E0E0E0' }} data-testid="team-list">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold" style={{ color: '#1A1209' }}>
              Team Members ({team.length})
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <Loader2 size={24} className="animate-spin mx-auto" style={{ color: '#F5831F' }} />
            </div>
          ) : team.length === 0 ? (
            <div className="p-8 text-center">
              <UserPlus size={40} className="mx-auto mb-3" style={{ color: '#E0E0E0' }} />
              <p className="text-sm font-medium" style={{ color: '#333' }}>No team members yet</p>
              <p className="text-xs mt-1" style={{ color: '#6B6257' }}>Invite your first sub-admin to get started.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {team.map(member => {
                const domainColor = DOMAIN_COLORS[member.admin_domain] || '#666';
                const isActive = member.status === 'active';
                const isInvited = member.status === 'invited';

                return (
                  <div key={member.user_id} className="px-5 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-testid={`team-member-${member.user_id}`}>
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: domainColor }}>
                        {(member.name || '?')[0].toUpperCase()}
                      </div>

                      {/* Info */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate" style={{ color: '#1A1209' }}>{member.name}</p>
                          <span className={`px-2 py-0.5 text-[10px] rounded-full font-medium ${isActive ? 'bg-green-100 text-green-700' : isInvited ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                            {member.status || 'invited'}
                          </span>
                        </div>
                        <p className="text-xs mt-0.5" style={{ color: '#6B6257' }}>{member.email}</p>
                      </div>

                      {/* Domain badge */}
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-medium" style={{ backgroundColor: `${domainColor}15`, color: domainColor }}>
                        {DOMAIN_LABELS[member.admin_domain] || member.admin_domain}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 ml-4">
                      {member.status === 'active' && (
                        <button onClick={() => updateMember(member.user_id, { status: 'deactivated' })}
                          className="px-2.5 py-1 text-[11px] rounded-md border font-medium text-amber-600 border-amber-200 hover:bg-amber-50"
                          data-testid={`deactivate-${member.user_id}`}>
                          Deactivate
                        </button>
                      )}
                      {member.status === 'deactivated' && (
                        <button onClick={() => updateMember(member.user_id, { status: 'active' })}
                          className="px-2.5 py-1 text-[11px] rounded-md border font-medium text-green-600 border-green-200 hover:bg-green-50"
                          data-testid={`reactivate-${member.user_id}`}>
                          Reactivate
                        </button>
                      )}
                      <button onClick={() => removeMember(member.user_id, member.name)}
                        className="p-1.5 rounded-md border border-red-200 text-red-400 hover:bg-red-50 hover:text-red-600"
                        data-testid={`remove-${member.user_id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Domain Guide */}
        <div className="bg-white rounded-xl p-5 shadow-sm" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: '#1A1209' }}>Domain Access Guide</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(DOMAIN_LABELS).map(([id, label]) => (
              <div key={id} className="p-3 rounded-lg" style={{ backgroundColor: `${DOMAIN_COLORS[id]}08`, border: `1px solid ${DOMAIN_COLORS[id]}20` }}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: DOMAIN_COLORS[id] }} />
                  <span className="text-xs font-bold" style={{ color: DOMAIN_COLORS[id] }}>{label}</span>
                </div>
                <p className="text-[10px]" style={{ color: '#6B6257' }}>
                  {id === 'schools' && 'School applications, dashboard, enrollment codes'}
                  {id === 'corporates' && 'Corporate registrations, onboarding, wellness reports'}
                  {id === 'users' && 'User profiles, teen safety, parental controls'}
                  {id === 'leads' && 'Reddit discovery, auto-responder, templates'}
                  {id === 'content' && 'AvA images, media library, notifications'}
                  {id === 'analytics' && 'Platform metrics, revenue, growth reports'}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTeam;
